import React, { useState, useEffect, useRef, useMemo } from 'react';
import cx from 'classnames';
import Fonts from '../../../fonts/';
import { useActionExectioner } from '../../core/actions';
import TruncatedTextWithTooltip from '../../../../components/TruncatedTextWithTooltip';

import { getColorsConfig } from '../../../../components/AssetViewer/helper/cssHelper';
import { injectAktivFallback, isActionTriggerLoading, SPINNER_CLASS } from '../../../widgets/widget.helper';

import styles from './menuContainer.module.scss';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';



const MenuButtonComponent = ({ className, widgetProps, breakpoint, menuContent, closeMenuItem }) => {
  const trayBackground = breakpoint !== 'mobile' ? 'rgba(255, 255, 255, 0)' : getColorStyles(get(widgetProps, `properties.${breakpoint}.trayBackground`))
  Fonts.useFont(get(widgetProps, 'fonts') || []);

  return (
    <div
      className={cx(className,
        { [styles.buttonContainer]: breakpoint === 'desktop' },
        { [styles.verticalView]: breakpoint === 'mobile' },
      )}
      style={{ backgroundColor: trayBackground }}
    >
      {
        (menuContent || []).map((menu, index) => {
          if (menu.children && menu.children.length > 0) {
            return <DropdownMenu index={index} key={index} menu={menu} contentLength={menuContent.length} widgetProperties={widgetProps} breakpoint={breakpoint} closeMenuItem={closeMenuItem}/>
          } else {
            return <MenuItem index={index} key={index} menu={menu} contentLength={menuContent.length} widgetProperties={widgetProps} breakpoint={breakpoint} closeMenuItem={closeMenuItem}/>
          }
        })
      }
    </div>
  );
};

const MenuItem = ({ menu, contentLength, index, breakpoint, widgetProperties, closeMenuItem }) => {
  const actionExcutioner = useActionExectioner();
  const [isHovered, setIsHovered] = useState(false);
  const widgetProps = get(widgetProperties, 'properties');
  const trayAlignment = get(widgetProps, 'mobile.trayAlignment.position');
  const action = get(menu, 'action');

  const handleClick = (e) => {
    if (action) {
      e.stopPropagation();
      actionExcutioner.execute(widgetProperties, action);
      closeMenuItem();
    }
  }

  const buttonStyles = useMemo(() => {
    return getStyles(widgetProps, breakpoint, isHovered ? 'hover' : 'regular');
  }, [widgetProps, breakpoint, isHovered]);

  return (
    <div
      key={index}
      className={cx(styles.menuButton, {
        [styles.lastButton]: index === contentLength - 1, 
        [SPINNER_CLASS]: isActionTriggerLoading(action)
       })}
      style={buttonStyles}
      onClick={handleClick}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
    >
      {(trayAlignment === "left" || trayAlignment === "right") && breakpoint === "mobile" ?
       <TruncatedTextWithTooltip content={menu.name} maxWidth={240} placement='top'/> : menu.name
      }
    </div>
  )
}

const DropdownMenu = ({ menu, contentLength, index, widgetProperties, breakpoint, closeMenuItem }) => {
  const widgetProps = get(widgetProperties, 'properties');
  const subMenuRef = useRef();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const buttonStyle = useMemo(() => {
    return getStyles(widgetProps, breakpoint, isHovered ? 'hover' : 'regular');
  }, [widgetProps, breakpoint, isHovered]);

  const handleOnMouseOver = (show, hovered) => {
    setShowDropdown(show);
    setIsHovered(hovered);
  }

  const toggleDropDown = (show) => {
    setShowDropdown(show);
    setIsHovered(show);
  }

  const adjustSubMenuPosition = () => {
    if (!subMenuRef.current) return;
    
    const submenuRect = subMenuRef.current.getBoundingClientRect();
    const overFlowLeft = Math.round(submenuRect.left);
    const overFlowRight = (overFlowLeft + subMenuRef.current.scrollWidth);

    if (overFlowRight > window.innerWidth) {
      //Apply the translateX value to the submenu's transform property
      subMenuRef.current.style.transform = `translateX(-${overFlowRight - window.innerWidth}px)`;
    }
  }

  useEffect(() => {
    if (showDropdown && breakpoint === 'desktop') {
      adjustSubMenuPosition()
    }
  }, [showDropdown, breakpoint])

  if (breakpoint === 'mobile') {
    return (
      <MobileDropDownMenu
        menu={menu}
        index={index}
        buttonStyle={buttonStyle}
        showDropdown={showDropdown}
        widgetProps={widgetProps}
        toggleDropDown={toggleDropDown}
        handleOnMouseOver={handleOnMouseOver}
        contentLength={contentLength}
        closeMenuItem={closeMenuItem}
      />
    )
  }

  return (
    <div
      onMouseLeave={() => handleOnMouseOver(false, false)}
      onMouseEnter={() => handleOnMouseOver(true, true)}
      className={styles.dropdownWrapper}
    >
      <button
        className={cx(styles.menuButton, { [styles.lastButton]: index === contentLength - 1 })}
        style={buttonStyle}
        key={index}
        onClick={() => toggleDropDown(!showDropdown)}
      >
        {menu.name}
        <i className={cx('cleverstory-drop-down', styles.dropDownIcon, { [styles.showDropdown]: showDropdown })} />
      </button>
      {showDropdown &&
        <div ref={subMenuRef} className={styles.dropdownMenu}>
          {
            (menu.children || []).map((submenu, index) => {
              return (
                <SubMenuOption key={index} widgetProps={widgetProps} breakpoint="desktop" action={submenu.action} name={submenu.name} index={index} showDropdown={showDropdown} toggleDropDown={toggleDropDown} closeMenuItem={closeMenuItem}/>
              )
            })
          }
        </div>
      }
    </div>
  )
}

const MobileDropDownMenu = ({ menu, index, buttonStyle, showDropdown, widgetProps, handleOnMouseOver, toggleDropDown, contentLength, closeMenuItem }) => {
  const spacing = get(buttonStyle, 'marginBottom');
  const trayAlignment = get(widgetProps, 'mobile.trayAlignment.position');
  
  const dropdownButtonStyle = useMemo(() => ({
    ...buttonStyle,
    marginBottom: '0px'
  }), [buttonStyle]);

  return (
    <div key={index}
      style={{ marginBottom: spacing }}
      onClick={() => toggleDropDown(!showDropdown)}
      onMouseLeave={() => handleOnMouseOver(showDropdown, false)}
      onMouseEnter={() => handleOnMouseOver(showDropdown, true)}
      className={cx(styles.mobileDropdownMenu)} id='menu'
    >
      <div className={cx(styles.mobileMenuButton, { [styles.lastButton]: index === contentLength - 1 })} style={dropdownButtonStyle}>
        <span className={styles.textContainer}>
        {(trayAlignment === "left" || trayAlignment === "right") ? 
        <TruncatedTextWithTooltip content={menu.name} maxWidth={240} placement='top'/> : menu.name
        }
        </span>
        <i className={cx('cleverstory-drop-down', { [styles.showDropdown]: showDropdown })} />
      </div>
      <ul className={cx(styles.listView, { [styles.active]: showDropdown })} id='listview'>
        {
          (menu.children || []).map((submenu, index) => {
            return (
              <SubMenuOption key={index} widgetProps={widgetProps} breakpoint='mobile' action={submenu.action} name={submenu.name} index={index} showDropdown={showDropdown} toggleDropDown={toggleDropDown} closeMenuItem={closeMenuItem}/>
            )
          })
        }
      </ul>
    </div>
  )
}

const SubMenuOption = ({ name, index, widgetProps, breakpoint, action, showDropdown, toggleDropDown, closeMenuItem }) => {
  const actionExcutioner = useActionExectioner();
  const [isHovered, setIsHovered] = useState(false);

  const subMenuStyle = useMemo(() => {
    return getDropDownStyles(widgetProps, isHovered ? 'hover' : 'regular', breakpoint);
  }, [widgetProps, breakpoint, isHovered]);

  const handleClick = (e) => {
    if (action) {
      e.stopPropagation();
      actionExcutioner.execute(widgetProps, action);
      toggleDropDown(!showDropdown)
      closeMenuItem();
    }
  }

  return (
    <div
      style={subMenuStyle}
      key={index}
      onMouseLeave={() => setIsHovered(false)}
      onMouseEnter={() => setIsHovered(true)}
      onClick={handleClick}
      className={cx(styles.dropdownItem, {
        [SPINNER_CLASS]: isActionTriggerLoading(action)
      })}
    >
      {name}
    </div>
  )
}

const menuButtonEquals = function (prevProps, nextProps) {
  const paths = ['className', 'widgetProps', 'breakpoint', 'menuContent', 'active'];
  const next = pick(nextProps, paths);
  const previous = pick(prevProps, paths);

  const equals = isEqual(next, previous);
  return equals;
}

export const MenuButton = React.memo(MenuButtonComponent, menuButtonEquals);

/**
 * Generate Styles
 *
*/
function getDropDownStyles(widgetProperties, active, breakpoint) {
  var styles = {};

  if (!widgetProperties) {
    return styles;
  }

  const defaultProperties = get(widgetProperties, `desktop`);
  const defaultStyles = get(defaultProperties, `regular.menuStyles`, {});

  //font-color
  const colorRgb = get(defaultProperties, `${active}.dropdownColor`, null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    color: color
  }

  //background
  const backgroundRgb = get(defaultProperties, `${active}.dropdownBackground`, null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = getColorsConfig(backgroundRgb);
  }

  styles['background'] = background;


  const font = get(defaultStyles, 'font', {});

  //font-family
  const fontFamily = get(font, 'fontFamily');
  if (fontFamily) {
    styles = {
      ...styles,
      fontFamily: injectAktivFallback(fontFamily)
    }
  }

  //line Height
  const layoutHeight = get(defaultProperties, 'layout.height');
  if (layoutHeight) {
    styles['lineHeight'] = layoutHeight;
  }

  //font-style
  const bold = get(font, 'style.bold', false);
  if (bold) {
    styles['fontWeight'] = 'bold';
  }

  const italic = get(font, 'style.italic', false);
  if (italic) {
    styles['fontStyle'] = 'italic';
  }

  const underline = get(font, 'style.underline', false);
  if (underline) {
    styles['textDecoration'] = 'underline';
  }

  //line spacing
  const lineSpacingString = get(defaultProperties, 'regular.menuStyles.font.lineSpacing', null);
  if (lineSpacingString) {
    const lineSpacing = parseFloat(lineSpacingString) + 'em';
    styles['lineHeight'] = lineSpacing;
  }

  //Letter Spacing
  const letterSpacingString = get(defaultProperties, 'regular.menuStyles.font.letterSpacing', null);
  if (letterSpacingString) {
    const letterSpacing = parseFloat(letterSpacingString) + 'em';
    styles['letterSpacing'] = letterSpacing;
  }

  //font-size
  const fontSizeString = breakpoint === 'desktop' ? get(defaultProperties, `regular.menuStyles.font.fontSize`, null) : 14;
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px';
  }

  styles['fontSize'] = fontSize;
  styles['justifyContent'] = 'center';
  styles['textAlign'] = 'center';


  //border
  const border = get(defaultProperties, `${active}.dropdownBorder`, {});
  const borderColor = get(border, `color`, null);

  const borderStyle = getBorderStyles(border, borderColor);
  styles = {
    ...styles,
    ...borderStyle
  }

  return styles;
}

function getStyles(properties, breakpoint, active) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = get(properties, `desktop`);
  const deviceProperties = get(properties, `${breakpoint}`);
  const defaultStyles = get(defaultProperties, `regular.menuStyles`, {});

  //font-color
  const colorRgb = get(defaultProperties, `${active}.menuStyles.font.color`, null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    color: color
  }

  //background
  const backgroundRgb = get(defaultProperties, `${active}.menuStyles.background`, null);
  var background = 'inherit';
  if (backgroundRgb) {
    background = getColorsConfig(backgroundRgb);
  }

  styles['background'] = background;

  const font = get(defaultStyles, 'font', {});

  //font-family
  const fontFamily = get(font, 'fontFamily');
  if (fontFamily) {
    styles = {
      ...styles,
      fontFamily: injectAktivFallback(fontFamily)
    }
  }

  //font-size
  const fontSizeString = get(deviceProperties, 'regular.menuStyles.font.fontSize', null);
  var fontSize = 'inherit'
  if (fontSizeString) {
    fontSize = parseInt(fontSizeString) + 'px';
  }

  styles['fontSize'] = fontSize;

  //Letter Spacing
  const letterSpacingString = get(deviceProperties, 'regular.menuStyles.font.letterSpacing', null);
  if (letterSpacingString) {
    const letterSpacing = parseFloat(letterSpacingString) + 'em';
    styles['letterSpacing'] = letterSpacing;
  }

  //line Height
  const layoutHeight = get(deviceProperties, 'layout.height');
  if (layoutHeight && breakpoint === 'desktop') {
    // styles['lineHeight'] = layoutHeight;
  }

  //line spacing
  const lineSpacingString = get(deviceProperties, 'regular.menuStyles.font.lineSpacing', null);
  if (lineSpacingString) {
    const lineSpacing = parseFloat(lineSpacingString) + 'em';
    styles['lineHeight'] = lineSpacing;
  }


  //font-style
  const bold = get(font, 'style.bold', false);
  if (bold) {
    styles['fontWeight'] = 'bold';
  }

  const italic = get(font, 'style.italic', false);
  if (italic) {
    styles['fontStyle'] = 'italic';
  }

  const underline = get(font, 'style.underline', false);
  if (underline) {
    styles['textDecoration'] = 'underline';
  }

  //border
  const border = get(defaultProperties, `${active}.menuStyles.border`, {});
  const borderColor = get(defaultProperties, `${active}.menuStyles.border.color`, null);

  const borderStyle = getBorderStyles(border, borderColor);
  styles = {
    ...styles,
    ...borderStyle
  }

  // spacing & padding
  const spacing = get(defaultProperties, 'padding');
  const horizontalPadding = get(defaultProperties, 'itemHorizontalPadding');
  const verticalPadding = get(defaultProperties, 'itemVerticalPadding');

  styles = {
    ...styles,
    paddingLeft: `${horizontalPadding}px`,
    paddingRight: `${horizontalPadding}px`,
    paddingTop: `${verticalPadding}px`,
    paddingBottom: `${verticalPadding}px`
  }

  if (breakpoint === 'mobile') {
    styles = {
      ...styles,
      marginBottom: `${spacing}px`,
    }
  } else if (breakpoint === 'desktop') {
    styles = {
      ...styles,
      marginRight: `${spacing}px`,
    }
  }

  //alignment
  const alignment = get(deviceProperties, 'alignment', {});

  var position = get(alignment, 'position', 'center');
  styles['textAlign'] = position;

  var margin = get(alignment, 'margin', 0);
  if (position === 'left') {
    styles = {
      ...styles,
      paddingLeft: `${margin}px`,
      justifyContent: 'flex-start'
    }
  } else if (position === 'right') {
    styles = {
      ...styles,
      paddingRight: `${margin}px`,
      justifyContent: 'flex-end'
    }
  }

  return styles;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = 'inherit';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  return color;
}

export function getBorderStyles(value, colorRgb) {
  if (!value) {
    return null;
  }

  var styles = {};
  const topLeft = get(value, 'radius.topLeft', null);
  const topRight = get(value, 'radius.topRight', null);
  const bottomLeft = get(value, 'radius.bottomLeft', null);
  const bottomRight = get(value, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  var color = 'transparent';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  styles['borderColor'] = color;

  const borderWidthString = get(value, 'width', null);
  var borderWidget = parseInt(borderWidthString) + 'px';

  styles['borderWidth'] = borderWidget;

  return styles;
}
